import {
  Badge,
  IconButton,
  Menu,
  ListItemText,
  Typography,
  Divider,
  ListItemIcon,
  ListItem,
  List,
  Button,
  Box,
  Tooltip,
  Skeleton,
} from "@mui/material";
import NotificationsIcon from "@mui/icons-material/Notifications";
import { useRef, useState } from "react";
import { observer } from "mobx-react-lite";
import { useStore } from "stores/useStore";
import { CheckCircle, Error, Info, Warning } from "@mui/icons-material";
import dayjs from "dayjs";

const getNotificationIcon = (type: string) => {
  switch (type) {
    case "error":
      return <Error color="error" />;
    case "warning":
      return <Warning color="warning" />;
    case "success":
      return <CheckCircle color="success" />;
    case "info":
    default:
      return <Info color="info" />;
  }
};

const NotificationIcon = observer(() => {
  const { notificationStore, authStore } = useStore();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const listRef = useRef<HTMLUListElement | null>(null);

  const open = Boolean(anchorEl);

  const handleOpen = async (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
    if (authStore?.user?.companyId) {
      await notificationStore.fetchNotifications(authStore.user.companyId);
    }
  };
  const handleClose = () => setAnchorEl(null);

  const handleNotificationClick = async (notifId: number) => {
    await notificationStore.markNotificationAsRead(notifId);
    handleClose();
  };

  const markAllRead = async () => {
    if (!authStore.user?.companyId) return;
    await notificationStore.markAllAsRead(authStore.user.companyId);
  };

  const handleScroll = async (e: React.UIEvent<HTMLUListElement>) => {
    const target = e.currentTarget;
    if (
      target.scrollHeight - target.scrollTop === target.clientHeight &&
      !notificationStore.loading &&
      notificationStore.notifications.length >=
        notificationStore.page * notificationStore.limit
    ) {
      if (authStore?.user?.companyId) {
        await notificationStore.fetchNotifications(
          authStore.user.companyId,
          notificationStore.page + 1
        );
        notificationStore.page += 1;
      }
    }
  };

  return (
    <>
      <Tooltip title="Notifications">
        <IconButton
          color="inherit"
          onClick={handleOpen}
          size="large"
          aria-label="show notifications"
          aria-controls="notification-menu"
          aria-haspopup="true"
        >
          <Badge badgeContent={notificationStore.unreadCount} color="error">
            <NotificationsIcon />
          </Badge>
        </IconButton>
      </Tooltip>

      <Menu
        id="notification-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        slotProps={{
          paper: {
            sx: { width: 620, maxHeight: 470, p: 0 },
          },
        }}
      >
        <Box
          sx={{
            px: 2,
            py: 1,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="h6">Notifications</Typography>
          <Button
            variant="text"
            size="small"
            disabled={notificationStore.unreadCount === 0}
            onClick={markAllRead}
          >
            Marquer tout lu
          </Button>
        </Box>
        <Divider />

        <List
          ref={listRef}
          dense
          onScroll={handleScroll}
          sx={{
            maxHeight: 400,
            overflowY: "auto",
            "&::-webkit-scrollbar": { width: 6 },
            "&::-webkit-scrollbar-thumb": {
              bgcolor: "grey.400",
              borderRadius: 3,
            },
          }}
        >
          {notificationStore.loading &&
          notificationStore.notifications.length === 0 ? (
            Array.from({ length: 5 }).map((_, i) => (
              <ListItem key={i} sx={{ py: 1, px: 2 }}>
                <ListItemIcon>
                  <Skeleton variant="circular" width={24} height={24} />
                </ListItemIcon>
                <ListItemText
                  primary={<Skeleton width="80%" height={20} />}
                  secondary={<Skeleton width="40%" height={16} />}
                />
              </ListItem>
            ))
          ) : notificationStore.notifications.length === 0 ? (
            <Typography
              sx={{ p: 2, textAlign: "center", color: "text.secondary" }}
            >
              Aucune notification
            </Typography>
          ) : (
            <>
              {notificationStore.notifications.map((notif) => (
                <ListItem
                  key={notif.id}
                  component="button"
                  onClick={() => handleNotificationClick(notif.id)}
                  sx={{
                    bgcolor: notif.isRead
                      ? "background.paper"
                      : "action.selected",
                    fontWeight: !notif.isRead ? "bold" : "normal",
                    "&:hover": { bgcolor: "action.hover", boxShadow: 1 },
                    borderRadius: 1,
                    my: 0.25,
                    border: "none",
                    textAlign: "left",
                  }}
                >
                  <ListItemIcon>
                    {getNotificationIcon(notif.type || "info")}
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Tooltip title={notif.text}>
                        <Typography
                          noWrap
                          fontWeight={notif.isRead ? "regular" : "bold"}
                          sx={{
                            maxWidth: 550,
                            whiteSpace: "normal",
                            cursor: "pointer",
                          }}
                        >
                          {notif.text}
                        </Typography>
                      </Tooltip>
                    }
                    secondary={dayjs(notif.createdAt).format(
                      "DD/MM/YYYY HH:mm"
                    )}
                  />
                </ListItem>
              ))}
            </>
          )}

          <ListItem sx={{ justifyContent: "center", py: 1 }}>
            {!notificationStore.loading &&
              notificationStore.notifications.length > 0 &&
              (notificationStore.notifications.length <
              notificationStore.page * notificationStore.limit ? (
                <Typography variant="body2" color="text.secondary">
                  Toutes les notifications sont affichées
                </Typography>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Faites défiler vers le bas pour charger plus
                </Typography>
              ))}
          </ListItem>
        </List>
      </Menu>
    </>
  );
});

export default NotificationIcon;
